import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { useTheme } from "../contexts/theme-provider";
import { useAuth } from "../contexts/auth-context";

import {
  FileSpreadsheet,
  AlertCircle,
  Sparkles,
  CheckCircle2,
  Brain,
  BarChart3,
  TrendingUp,
  Cpu
} from "lucide-react";
import { handleProcessReportFile } from "../services/reports"; 

interface CreateReportPageProps {
  fileData: File | null;
  reportTypes: {reportType: string; reportType2: string} | null;
  onReportCreated: (reportData: any) => void;
}

export default function CreateReportPage({ fileData, reportTypes, onReportCreated }: CreateReportPageProps) {
  const { user } = useAuth();
  const { theme } = useTheme();
  const reportRef = useRef<HTMLDivElement | null>(null);

  const [reportName, setReportName] = useState("");
  const [otherInfo, setOtherInfo] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingStage, setProcessingStage] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Stages of processing with messages
  const processingStages = [
    "Uploading file to secure server...",
    "Analyzing data structure...",
    "Running AI algorithms...",
    "Generating insights...",
    "Finalizing report..."
  ];

  // Simulate progress stages
  useEffect(() => {
    let interval: ReturnType<typeof setTimeout>;
    if (isProcessing) {
      interval = setInterval(() => {
        setProcessingStage(prev => {
          if (prev >= processingStages.length - 1) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 2000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !user.id) {
      setError("You must be logged in to create a report.");
      return;
    }
    if (!reportName) {
      setError("Please provide a name for the report.");
      return;
    }
    if (!reportTypes) {
      setError("Report types are missing.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProcessingStage(0);

    abortControllerRef.current = new AbortController();

    try {
      let finalName = reportName.trim();
      if (!finalName.toLowerCase().endsWith(".xlsx")) {
        finalName += ".xlsx";
      }

      const renamedFile = new File([fileData!], finalName, { type: fileData!.type });

      // Create form data with report types
      const formData = new FormData();
      formData.append("file", fileData!);
      formData.append("user_id", user.id);
      formData.append("report_name", reportName);
      formData.append("report_type", reportTypes.reportType);
      formData.append("report_type_2", reportTypes.reportType2);

      if (otherInfo) {
        formData.append("other_info", otherInfo);
      }

      const apiResponse = await handleProcessReportFile(
        formData,
        user.id,
        abortControllerRef.current.signal,
        user.access_token!
      );

      const fullReportData = {
        ...apiResponse,
        reportName,
        otherInfo,
        reportType: reportTypes.reportType,
        reportType2: reportTypes.reportType2,
        originalFileName: fileData!.name,
        sentFileName: renamedFile.name,
        fileSize: fileData!.size,
        createdAt: new Date().toISOString(),
        inputBlobName: apiResponse._debug?.input_file,
        outputBlobName: apiResponse._debug?.output_file,
      };

      sessionStorage.setItem("currentReport", JSON.stringify(fullReportData));
      onReportCreated(fullReportData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "An unknown error occurred.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!fileData || !reportTypes) {
    return <div>Error: No file data or report types provided. Please go back.</div>;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, when: "beforeChildren" },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  // Animation variants for processing screen
  const floatingOrbVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 3,
        repeat: Number.POSITIVE_INFINITY,
      }
    }
  };

  const pulseVariants = {
    animate: {
      scale: [1, 1.1, 1],
      opacity: [0.7, 1, 0.7],
      transition: {
        duration: 2,
        repeat: Number.POSITIVE_INFINITY,
      }
    }
  };

  return (
    <motion.div
      ref={reportRef}
      className="scroll-mt-20"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div
        className={`min-h-screen ${
          theme === "dark"
            ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
            : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
        }`}
      >
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md"
            >
              <div className={`absolute inset-0 ${theme === "dark" ? "bg-black/70" : "bg-white/90 dark:bg-slate-900/90"}`}></div>
              
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 15 }}
                className={`relative z-10 w-full max-w-2xl mx-4 p-8 rounded-3xl shadow-2xl ${
                  theme === "dark" 
                    ? "bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700" 
                    : "bg-gradient-to-br from-white to-blue-50 border border-blue-100"
                }`}
              >
                <div className="text-center mb-8">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center"
                  >
                    <Brain className="h-10 w-10 text-white" />
                  </motion.div>
                  
                  <motion.h2 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`text-2xl font-bold mb-4 ${
                      theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"
                    }`}
                  >
                    AI Analysis in Progress
                  </motion.h2>
                  
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className={`mb-2 ${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}
                  >
                    {processingStages[processingStage]}
                  </motion.p>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-6 dark:bg-gray-700 overflow-hidden">
                    <motion.div 
                      initial={{ width: "0%" }}
                      animate={{ width: `${((processingStage + 1) / processingStages.length) * 100}%` }}
                      transition={{ duration: 0.5 }}
                      className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600"
                    ></motion.div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[Cpu, BarChart3, TrendingUp].map((Icon, index) => (
                    <motion.div
                      key={index}
                      variants={floatingOrbVariants}
                      animate="animate"
                      transition={{ delay: index * 0.3 }}
                      className={`p-4 rounded-2xl flex flex-col items-center ${
                        theme === "dark" 
                          ? "bg-gray-700/50" 
                          : "bg-blue-100/50"
                      }`}
                    >
                      <motion.div
                        variants={pulseVariants}
                        animate="animate"
                        transition={{ delay: index * 0.5 }}
                        className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center mb-2"
                      >
                        <Icon className="h-6 w-6 text-white" />
                      </motion.div>
                      <span className={`text-xs ${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}>
                        {["Processing", "Analyzing", "Optimizing"][index]}
                      </span>
                    </motion.div>
                  ))}
                </div>
                
                <div className="flex justify-center">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.05, 1],
                      transition: { duration: 2, repeat: Infinity }
                    }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsProcessing(false);
                        setProcessingStage(0);
                      }}
                      className={`rounded-full ${
                        theme === "dark" 
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700" 
                          : "border-gray-300 text-gray-700 dark:text-slate-200 hover:bg-gray-100"
                      }`}
                    >
                      Cancel Processing
                    </Button>
                  </motion.div>
                </div>
                
                {/* Floating elements in background */}
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute rounded-full ${
                      theme === "dark" 
                        ? "bg-blue-500/10" 
                        : "bg-blue-300/30 dark:bg-blue-800/40"
                    }`}
                    style={{
                      width: Math.random() * 60 + 20,
                      height: Math.random() * 60 + 20,
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -30, 0],
                      rotate: [0, 180, 360],
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: Math.random() * 10 + 10,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div className="p-8" variants={containerVariants} initial="hidden" animate="visible">
          <div className="max-w-3xl mx-auto space-y-8">
            {/* Header */}
            <motion.div className="flex flex-col items-center gap-4 text-center" variants={itemVariants}>
              <h1 className={`text-4xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"}`}>
                Create Analysis Report
              </h1>
              <p className={`text-lg ${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}>
                Configure your report settings and let AI work its magic
              </p>
            </motion.div>

            {/* File Info */}
            <motion.div variants={itemVariants}>
              <Card className={`border-0 shadow-xl backdrop-blur-sm ${theme === "dark" ? "bg-gray-800/50" : "bg-white/70"}`}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-3 text-xl ${theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"}`}>
                    <motion.div
                      className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center"
                      whileHover={{ rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <FileSpreadsheet className="h-5 w-5 text-white" />
                    </motion.div>
                    Uploaded File & Report Types
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <motion.div
                    className={`flex items-center justify-between p-4 rounded-xl ${theme === "dark" ? "bg-gray-700/50" : "bg-gray-50"}`}
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div>
                      <p className={`font-semibold text-lg ${theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"}`}>
                        {fileData.name}
                      </p>
                      <p className={`text-sm ${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}>
                        {(fileData.size / 1024 / 1024).toFixed(2)} MB •{" "}
                        {new Date(fileData.lastModified).toLocaleDateString()}
                      </p>
                    </div>
                    <motion.div
                      className="flex items-center gap-2 text-green-500 dark:text-green-400 font-semibold"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
                    >
                      <CheckCircle2 className="h-5 w-5" />
                      Validated
                    </motion.div>
                  </motion.div>

                  {/* Report Types Display */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${theme === "dark" ? "bg-gray-700/30" : "bg-blue-50/50"}`}>
                      <p className={`text-sm font-medium ${theme === "dark" ? "text-gray-400" : "text-gray-600 dark:text-slate-400"}`}>
                        Report Type
                      </p>
                      <p className={`font-semibold ${theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"}`}>
                        {reportTypes.reportType}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === "dark" ? "bg-gray-700/30" : "bg-purple-50/50"}`}>
                      <p className={`text-sm font-medium ${theme === "dark" ? "text-gray-400" : "text-gray-600 dark:text-slate-400"}`}>
                        Sub Category
                      </p>
                      <p className={`font-semibold ${theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"}`}>
                        {reportTypes.reportType2}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Report Configuration Form */}
            <motion.div variants={itemVariants}>
              <Card className={`border-0 shadow-xl backdrop-blur-sm ${theme === "dark" ? "bg-gray-800/50" : "bg-white/70"}`}>
                <CardHeader>
                  <CardTitle className={`text-xl flex items-center gap-3 ${theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"}`}>
                    <motion.div
                      className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center"
                      whileHover={{ rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <Sparkles className="h-5 w-5 text-white" />
                    </motion.div>
                    Report Configuration
                  </CardTitle>
                  <CardDescription className={`${theme === "dark" ? "text-gray-300" : "text-gray-600 dark:text-slate-400"}`}>
                    Provide details for your AI-powered analysis report
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <motion.div
                      className="space-y-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Label htmlFor="reportName" className={`text-sm font-semibold ${theme === "dark" ? "text-gray-200" : "text-gray-700 dark:text-slate-200"}`}>
                        Report Name *
                      </Label>
                      <Input
                        id="reportName"
                        type="text"
                        placeholder="Enter a descriptive name for your report"
                        value={reportName}
                        onChange={(e) => setReportName(e.target.value)}
                        disabled={isProcessing}
                        required
                        maxLength={100}
                        className={`h-12 text-base ${theme === "dark" ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-slate-400 focus:border-blue-500"}`}
                      />
                      <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400"}`}>
                        Choose a name that helps you identify this report later
                      </p>
                    </motion.div>

                    <motion.div
                      className="space-y-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Label htmlFor="otherInfo" className={`text-sm font-semibold ${theme === "dark" ? "text-gray-200" : "text-gray-700 dark:text-slate-200"}`}>
                        Additional Information
                      </Label>
                      <Textarea
                        id="otherInfo"
                        placeholder="Add any additional context, notes, or specific requirements for this analysis..."
                        value={otherInfo}
                        onChange={(e) => setOtherInfo(e.target.value)}
                        disabled={isProcessing}
                        rows={4}
                        maxLength={500}
                        className={`text-base resize-none ${theme === "dark" ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-slate-400 focus:border-blue-500"}`}
                      />
                      <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400"}`}>
                        Optional: Provide context that might help improve the analysis
                      </p>
                    </motion.div>

                    {error && (
                      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                        <Alert variant="destructive" className="border-red-500/50 bg-red-500/10">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="text-red-400">{error}</AlertDescription>
                        </Alert>
                      </motion.div>
                    )}

                    <motion.div
                      className="flex gap-4 pt-6"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex-1">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => window.location.reload()}
                          disabled={isProcessing}
                          className={`w-full h-12 text-base ${theme === "dark" ? "border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white" : "border-gray-300 text-gray-700 dark:text-slate-200 hover:bg-gray-50"}`}
                        >
                          Cancel
                        </Button>
                      </motion.div>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex-[2]">
                        <Button
                          type="submit"
                          disabled={isProcessing}
                          className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 shadow-lg"
                        >
                          {isProcessing ? (
                            <>
                              <motion.div
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                              />
                              Creating Report...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-5 w-5 mr-2" />
                              Start AI Analysis
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </motion.div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}